import Highcharts from 'highcharts';
import exporting from 'highcharts/modules/exporting';
import exportingOffline from 'highcharts/modules/offline-exporting';
import exportData from 'highcharts/modules/export-data';
import { formatKpiValue, resolveCurrencyDisplayScale, VALUE_DECIMALS } from './liveDashboardModel';

export const CHART_BLUE = '#2251FF';

let highchartsModulesInitialized = false;

/** Register Highcharts modules once (exporting + CSV data download). */
export const ensureLiveDashboardHighchartsModules = () => {
  if (highchartsModulesInitialized) {
    return;
  }
  exporting(Highcharts);
  exportingOffline(Highcharts);
  exportData(Highcharts);
  highchartsModulesInitialized = true;
};

const SHARED_EXPORTING = {
  enabled: true,
  fallbackToExportServer: false,
  buttons: {
    contextButton: {
      enabled: false,
    },
  },
  csv: {
    dateFormat: '%Y',
  },
};

const formatLiveDashboardYAxisLabel = function formatLiveDashboardYAxisLabel() {
  return Highcharts.numberFormat(this.value, VALUE_DECIMALS, '.', ',');
};

const createBarYAxisLabelFormatter = (toDisplay) => function formatBarYAxisLabel() {
  const displayValue = toDisplay(this.value);
  if (displayValue == null || Number.isNaN(displayValue)) {
    return '';
  }
  return Highcharts.numberFormat(displayValue, VALUE_DECIMALS, '.', ',');
};

const formatBarChartTooltipHtml = (metricLabel, yearLabel, rawValue, displayUnit, currencyCode, toDisplay) => {
  const displayValue = toDisplay?.(rawValue);
  if (displayValue == null || Number.isNaN(displayValue)) {
    return false;
  }
  const formattedValue = Highcharts.numberFormat(displayValue, VALUE_DECIMALS, '.', ',');

  const valueWithUnit = displayUnit
    ? `${formattedValue} ${displayUnit}`
    : formattedValue;

  return [
    '<div class="live-dashboard-kpi-tooltip">',
    `<div class="live-dashboard-kpi-tooltip-value">${valueWithUnit}</div>`,
    `<div>${metricLabel}</div>`,
    `<div>Year: ${yearLabel}</div>`,
    currencyCode ? `<div>${currencyCode}</div>` : '',
    '</div>',
  ].join('');
};

const createLiveDashboardBarTooltipFormatter = (displayUnit, currencyCode, metricLabel, toDisplay) =>
  function formatLiveDashboardBarTooltip() {
    const point = this;
    return formatBarChartTooltipHtml(metricLabel, point.key ?? point.x, point.y, displayUnit, currencyCode, toDisplay);
  };

/**
 * KPI summary chart (Highcharts Dashboards KPI / data-pool pattern).
 * Hidden visually but holds structured series data for CSV export.
 * @see https://www.highcharts.com/docs/dashboards/kpi-component
 */
export const createKpiSummaryChartOptions = ({
  kpiCards,
  currency,
  yearLabel,
}) => ({
  chart: {
    type: 'column',
    height: 1,
    styledMode: false,
    spacing: [0, 0, 0, 0],
    margin: [0, 0, 0, 0],
    backgroundColor: 'transparent',
  },
  title: {
    text: 'Market Snapshot KPIs',
  },
  subtitle: {
    text: `${yearLabel} · ${currency}`,
  },
  credits: { enabled: false },
  legend: { enabled: false },
  xAxis: {
    type: 'category',
    categories: kpiCards.map((kpi) => kpi.name),
    title: { text: 'Metric' },
    tickPixelInterval: 100,
  },
  yAxis: {
    type: 'linear',
    title: { text: 'Value' },
    tickPixelInterval: 72,
    gridLineWidth: 1,
    labels: {
      formatter: formatLiveDashboardYAxisLabel,
    },
  },
  tooltip: {
    useHTML: true,
    headerFormat: '<span style="font-size: 12px">{point.key}</span><br/>',
    pointFormat: '<b>{point.displayValue}</b>',
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      color: CHART_BLUE,
      dataLabels: {
        enabled: true,
        formatter() {
          return this.point.displayValue ?? '—';
        },
      },
    },
  },
  series: [{
    id: 'live-dashboard-kpi-summary',
    type: 'column',
    name: 'KPI values',
    data: kpiCards.map((kpi) => ({
      y: Number.isFinite(Number(kpi.value)) ? Number(kpi.value) : null,
      name: kpi.name,
      displayValue: kpi.displayValue,
    })),
  }],
  exporting: {
    ...SHARED_EXPORTING,
    filename: 'live-dashboard-kpis',
  },
});

/**
 * Historic bar chart with explicit category x-axis and linear y-axis.
 * @see https://www.highcharts.com/docs/chart-concepts/axes
 * @see https://www.highcharts.com/docs/chart-and-series-types/combining-chart-types
 */
export const createHistoricBarChartOptions = ({
  seriesId,
  metricLabel,
  seriesName,
  categories,
  data,
  currencyDisplayScale,
  displayUnit,
  currencyCode,
  minY,
  allowNegative = false,
}) => {
  const { toDisplay, unit } = currencyDisplayScale ?? resolveCurrencyDisplayScale(data);
  const resolvedDisplayUnit = displayUnit ?? unit;
  const resolvedCurrencyCode = currencyCode ?? '';

  return {
  chart: {
    type: 'column',
    height: 420,
  },
  title: { text: null },
  xAxis: {
    type: 'category',
    categories: [...categories],
    crosshair: true,
    tickPixelInterval: 100,
    gridLineWidth: 0,
    title: { text: '' },
  },
  yAxis: {
    type: 'linear',
    min: allowNegative ? null : (minY ?? 0),
    max: null,
    tickPixelInterval: 72,
    gridLineWidth: 1,
    startOnTick: true,
    endOnTick: true,
    ...(allowNegative
      ? {
        plotLines: [{
          color: '#dee2eb',
          value: 0,
          width: 1,
          zIndex: 3,
        }],
      }
      : {}),
    title: { text: '' },
    labels: {
      formatter: createBarYAxisLabelFormatter(toDisplay),
    },
  },
  legend: { enabled: false },
  tooltip: {
    useHTML: true,
    headerFormat: '',
    pointFormat: '',
    backgroundColor: '#ffffff',
    borderColor: '#dee2eb',
    borderRadius: 4,
    padding: 12,
    shadow: true,
    style: {
      color: '#333333',
    },
    formatter: createLiveDashboardBarTooltipFormatter(resolvedDisplayUnit, resolvedCurrencyCode, metricLabel, toDisplay),
  },
  plotOptions: {
    column: {
      borderWidth: 0,
      color: '#000000',
    },
  },
  series: [{
    id: seriesId,
    type: 'column',
    name: seriesName,
    data: [...data],
  }],
  credits: { enabled: false },
  exporting: {
    ...SHARED_EXPORTING,
    filename: `live-dashboard-${seriesName.replace(/\s+/g, '-').toLowerCase()}`,
  },
};
};

/** Combined chart: column historic series + optional spline overlay for export. */
export const createCombinedHistoricChartOptions = (barOptions, sparklineData = []) => {
  if (!sparklineData.length) {
    return barOptions;
  }

  return {
    ...barOptions,
    series: [
      ...(barOptions.series ?? []),
      {
        id: `${barOptions.series?.[0]?.id ?? 'bar'}-trend`,
        type: 'spline',
        name: `${barOptions.series?.[0]?.name ?? 'Series'} trend`,
        data: sparklineData,
        color: '#757575',
        lineWidth: 1,
        marker: { enabled: false },
        enableMouseTracking: false,
        showInLegend: false,
        yAxis: 0,
      },
    ],
  };
};

export const downloadChartCsv = (chart) => {
  if (chart?.downloadCSV) {
    chart.downloadCSV();
  }
};
